import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  Fragment,
} from "react";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Form from "react-bootstrap/Form";
import SimpleTable from "../../../components/atoms/table/simpleTable/simpleTable";
import Button from "../../../components/atoms/button/Button";
import "../../../styles/typography.css";
import SecondTab from "../../../components/organisms/secondTab";
import { toast, ToastContainer } from "react-toastify";
import { formatDate, getLocalDateString } from "../../../utility/Formatdate";
import {
  getHistoricalData,
  downloadHistoricalData,
} from "../Reports/reportsApi";

const HistoricalData = ({ TabConfig }) => {
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  // Filters
  const [fromDate, setFromDate] = useState(getLocalDateString(oneMonthAgo));
  const [toDate, setToDate] = useState(getLocalDateString(today));
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // Selection (now based on sampleId)
  const [selectedSampleIds, setSelectedSampleIds] = useState(new Set());

  // Pagination
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [reportsData, setReportsData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [paginationData, setPaginationData] = useState({});

  // Fetch data
  const fetchData = async (search, currentPage = 1, rowsPerPage = 25) => {
    setLoading(true);

    const payload = {
      FromDate: fromDate ? formatDate(new Date(fromDate)) : "",
      ToDate: toDate ? formatDate(new Date(toDate)) : "",
      PageNumber: currentPage,
      PageSize: rowsPerPage,
      ...(searchTerm && { FilterText: searchTerm }),
    };

    try {
      const res = await getHistoricalData(payload);
      const { totalRecords, pageNumber, pageSize, totalPages } = res;

      const formattedResults = Array.isArray(res.results)
        ? res.results.map((item) => ({
            ...item,
            sampleRequestDate: formatDate(item.sampleRequestDate),
            finalDate: formatDate(item.finalDate),
          }))
        : [];

      setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
      setReportsData(formattedResults);
      setTotalRecords(res.totalRecords);
    } catch (error) {
      console.error("Error fetching data:", error);
      setReportsData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, currentPage, rowsPerPage, searchTerm]);

  // Filter & search
  const filteredData = useMemo(() => {
    return (reportsData || []).filter((item) =>
      searchTerm
        ? Object.values(item).some(
            (val) =>
              val &&
              val.toString().toLowerCase().includes(searchTerm.toLowerCase())
          )
        : true
    );
  }, [reportsData, searchTerm]);

  const paginatedData = filteredData;

  // Toggle individual row selection (by sampleId)
  const toggleRowSelection = useCallback((sampleId) => {
    setSelectedSampleIds((prev) => {
      const copy = new Set(prev);
      copy.has(sampleId) ? copy.delete(sampleId) : copy.add(sampleId);
      return copy;
    });
  }, []);

  // Compute header checkbox state (based on sampleId)
  const isAllSelectedOnPage = useMemo(() => {
    return (
      paginatedData.length > 0 &&
      paginatedData.every((r) => selectedSampleIds.has(r.sampleId))
    );
  }, [paginatedData, selectedSampleIds]);

  // Toggle select all / deselect all 
  const toggleSelectAllOnPage = useCallback(() => {
    setSelectedSampleIds((prev) => {
      const copy = new Set(prev);
      const allSelected = paginatedData.every((r) => copy.has(r.sampleId));

      if (allSelected) {
        // Deselect all
        paginatedData.forEach((r) => copy.delete(r.sampleId));
      } else {
        // Select all
        paginatedData.forEach((r) => copy.add(r.sampleId));
      }
      return copy;
    });
  }, [paginatedData]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        header: (
          <Form.Check
            type="checkbox"
            checked={isAllSelectedOnPage}
            onChange={toggleSelectAllOnPage}
            aria-label="Select all"
          />
        ),
        accessor: "checkbox",
        disableSort: true,
        cell: (row) => (
          <Form.Check
            type="checkbox"
            checked={selectedSampleIds.has(row.sampleId)}
            onChange={() => toggleRowSelection(row.sampleId)}
            aria-label={`Select ${row.sampleId}`}
          />
        ),
      },
      { header: "Company", accessor: "companyName" },
      { header: "Location", accessor: "locationName" },
      { header: "Group ID", accessor: "groupId" },
      { header: "User Name", accessor: "finalBy" },
      { header: "Sample ID", accessor: "sampleId" },
      { header: "Customer Sample ID", accessor: "customerSampleId" },
      { header: "Request Date", accessor: "sampleRequestDate" },
      { header: "Complete Date", accessor: "finalDate" },
      { header: "Species", accessor: "speciesType" },
      { header: "Product", accessor: "drugType" },
      { header: "Lot No", accessor: "sampleLot" },
      { header: "Exp Level", accessor: "expectedLevel" },
      { header: "Result", accessor: "finalResult" },
    ],
    [isAllSelectedOnPage, selectedSampleIds, toggleRowSelection, toggleSelectAllOnPage]
  );

  const anySelected = selectedSampleIds.size > 0;

  const handleDownload = async () => {
    try {
      if (selectedSampleIds.size === 0) {
        toast.error("Please select at least one sample to download.");
        return;
      }

      const sampleIds = Array.from(selectedSampleIds);

      const params = {
        groupIds: [], 
        sampleIds,
      };

      const response = await downloadHistoricalData(params);
      const blob = new Blob([response], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "Historical-Lab-Data-Report.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Report downloaded successfully.");
    } catch (error) {
      toast.error("Failed to download report.");
    }
  };

  return (
    <Fragment>
      <SecondTab tabConfig={TabConfig} />
      <div className="p-3 typography">
        {/* --- Filters Row --- */}
        <div
          className="d-flex flex-wrap gap-3 shadow-lg mb-2 align-items-center p-3 mr-6"
          style={{ backgroundColor: "#fff", borderRadius: "5px" }}
        >
          <div className="ms-auto d-flex gap-2">
            <Button
              key="Download"
              size="md"
              variant={anySelected ? "active" : "disabled"}
              disabled={!anySelected}
              onClick={handleDownload}
            >
              Download
            </Button>
            <div
              className="line-separator"
              style={{
                width: "1px",
                height: "46px",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
              }}
            />
            <Form.Control
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              style={{ width: 180, height: 44, borderRadius: 5 }}
              size="sm"
            />
          </div>
        </div>

        <Card>
          <Card.Body className="p-0">
            {loading ? (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <Spinner animation="border" /> Loading...
              </div>
            ) : (
              <SimpleTable
                title=""
                columns={columns}
                data={paginatedData}
                customRowRender={(row) => (
                  <tr key={row.sampleId}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedSampleIds.has(row.sampleId)}
                        onChange={() => toggleRowSelection(row.sampleId)}
                        aria-label={`Select ${row.sampleId}`}
                      />
                    </td>
                    <td>{row.companyName}</td>
                    <td>{row.locationName}</td>
                    <td>{row.groupId}</td>
                    <td>{row.finalBy}</td>
                    <td>{row.sampleId}</td>
                    <td>{row.customerSampleId}</td>
                    <td>{row.sampleRequestDate}</td>
                    <td>{row.finalDate}</td>
                    <td>{row.speciesType}</td>
                    <td>{row.drugType}</td>
                    <td>{row.sampleLot}</td>
                    <td>{row.expectedLevel}</td>
                    <td>{row.finalResult}</td>
                  </tr>
                )}
                showPagination
                paginationData={paginationData}
                searchValue={searchTerm}
                fetchData={fetchData}
              />
            )}
          </Card.Body>
        </Card>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </Fragment>
  );
};

export default HistoricalData;
